import type {
    AnatomyBinding,
    AnatomyPart,
    AnatomyState,
    AnatomyVariant,
    ComponentAnatomySpec,
} from "./componentAnatomy";
import { switchAnatomySizes } from "./componentAnatomySwitchSizes";
import { anatomyTextStyle } from "./componentAnatomyText";

// Mined from designSystem/Switch/Switch.module.scss. Value, disabled, invalid
// and loading live on .switch; hover, press and focus live on .interactive.

const parts: readonly AnatomyPart[] = [
    {
        id: "border",
        label: "Track border",
        element: "1px border on .track, via --switch-track-border",
    },
    {
        id: "fill",
        label: "Track fill",
        element: "background on .track, via --switch-track-bg",
    },
    {
        id: "thumb",
        label: "Thumb",
        element:
            "background on .thumb (Base UI Switch.Thumb), via --switch-thumb-bg",
    },
    {
        id: "spinner",
        label: "Spinner",
        element:
            "SpinnerIcon inside .thumb, shown while loading, stroke via --switch-spinner-color",
    },
    {
        id: "focusRing",
        label: "Focus ring",
        element: "box-shadow on .track, from the shared focus-ring ring mixin",
    },
    {
        id: "label",
        label: "Label",
        element: "color and text style on .label",
    },
];

const states: readonly AnatomyState[] = [
    { id: "resting", label: "Default", selector: "" },
    {
        id: "hover",
        label: "Hover",
        selector: ":not([data-disabled], [data-readonly]):hover",
    },
    {
        id: "pressed",
        label: "Pressed",
        selector: ":not([data-disabled], [data-readonly]):active",
    },
    {
        id: "focus",
        label: "Focus",
        selector: ":not([data-disabled]):focus-visible",
    },
    { id: "invalid", label: "Invalid", selector: '[aria-invalid="true"]' },
    { id: "readOnly", label: "Read-only", selector: "[data-readonly]" },
    { id: "disabled", label: "Disabled", selector: "[data-disabled]" },
];

const fill = (token: `--${string}`): AnatomyBinding => ({
    partId: "fill",
    property: "background",
    token,
    via: "--switch-track-bg",
});

const edge = (token: `--${string}`, note?: string): AnatomyBinding => ({
    partId: "border",
    property: "border-color",
    token,
    via: "--switch-track-border",
    note,
});

const thumb = (token: `--${string}`): AnatomyBinding => ({
    partId: "thumb",
    property: "background",
    token,
    via: "--switch-thumb-bg",
});

const labelColor = (token: `--${string}`): AnatomyBinding => ({
    partId: "label",
    property: "color",
    token,
});

const thumbPosition = (isChecked: boolean): AnatomyBinding => ({
    partId: "thumb",
    property: "transform",
    literal: isChecked ? "translateX(var(--switch-thumb-travel))" : "none",
    note: isChecked
        ? "Slides over --duration-medium --ease-in-out from a pointer toggle; snaps from the keyboard."
        : undefined,
});

const spinnerOpacity = (isLoading: boolean): AnatomyBinding => ({
    partId: "spinner",
    property: "opacity",
    literal: isLoading ? "1" : "0",
    note: isLoading
        ? "Fades in over --duration-fast; the root is read-only and aria-busy."
        : "Always in the thumb, faded out and paused until loading.",
});

const focusRingBindings: readonly AnatomyBinding[] = [
    {
        partId: "focusRing",
        property: "box-shadow-spread",
        literal: "2px",
        note: "focus-ring.ring uses Sass $width, not --border-width-thick.",
    },
    {
        partId: "focusRing",
        property: "box-shadow-color",
        token: "--focus-ring-color",
        note: "focus-ring.ring uses Sass $blue-100, the same value this token carries.",
    },
];

const noFeedbackNote =
    "Read-only (and loading, which renders the root read-only) drops hover and pressed feedback.";

const sharedBindings: readonly AnatomyBinding[] = [
    { partId: "border", property: "border-width", token: "--border-width" },
    { partId: "border", property: "border-radius", token: "--radius-full" },
    {
        partId: "fill",
        property: "padding",
        literal: "1px",
        note: "ds-deviation: with the 1px border, keeps the thumb 2px from the outer edge.",
    },
    { partId: "thumb", property: "border-radius", token: "--radius-full" },
    {
        partId: "spinner",
        property: "stroke",
        token: "--fg-tertiary",
        via: "--switch-spinner-color",
    },
    {
        partId: "spinner",
        property: "stroke-width",
        literal: "1px",
        note: "vector-effect: non-scaling-stroke holds 1px at both sizes.",
    },
    {
        partId: "label",
        property: "column-gap",
        token: "--space-8",
        note: "Declared on the .switch row, spacing the track from its label.",
    },
    ...anatomyTextStyle({
        scale: "s",
        weight: "400",
        mixin: "text-s-14-regular",
    }),
];

const offResting: readonly AnatomyBinding[] = [
    fill("--bg-primary"),
    edge("--border-default"),
    thumb("--bg-neutral-strong"),
    labelColor("--fg-primary"),
];

const offStates: AnatomyVariant["stateBindings"] = {
    hover: [edge("--border-hover")],
    pressed: [fill("--bg-secondary"), edge("--border-hover")],
    focus: [edge("--border-accent-focus"), ...focusRingBindings],
    invalid: [
        edge(
            "--border-error",
            "Hover lifts it to --border-error-hover. The on state keeps its fill.",
        ),
    ],
    readOnly: [edge("--border-default", noFeedbackNote)],
    disabled: [
        edge("--border-disabled"),
        thumb("--bg-disabled"),
        labelColor("--fg-disabled"),
    ],
};

const onResting: readonly AnatomyBinding[] = [
    fill("--bg-accent"),
    edge("--bg-accent", "On states paint the border the fill colour."),
    thumb("--fg-on-solid"),
    labelColor("--fg-primary"),
];

const onStates: AnatomyVariant["stateBindings"] = {
    hover: [fill("--bg-accent-hover"), edge("--bg-accent-hover")],
    pressed: [fill("--bg-accent-pressed"), edge("--bg-accent-pressed")],
    focus: [
        edge("--bg-accent", "Focus keeps border = fill."),
        ...focusRingBindings,
    ],
    readOnly: [edge("--bg-accent", noFeedbackNote)],
    disabled: [
        fill("--bg-disabled"),
        edge("--bg-disabled"),
        thumb("--fg-on-solid"),
        labelColor("--fg-disabled"),
    ],
};

const loadingStates: AnatomyVariant["stateBindings"] = {
    hover: [edge("--border-default", noFeedbackNote)],
    pressed: [edge("--border-default", noFeedbackNote)],
    focus: [edge("--border-accent-focus"), ...focusRingBindings],
};

const variants: readonly AnatomyVariant[] = [
    {
        id: "off",
        label: "Off",
        selector: "[data-unchecked]",
        restingBindings: [
            ...offResting,
            thumbPosition(false),
            spinnerOpacity(false),
        ],
        stateBindings: offStates,
    },
    {
        id: "on",
        label: "On",
        selector: "[data-checked]",
        restingBindings: [
            ...onResting,
            thumbPosition(true),
            spinnerOpacity(false),
        ],
        stateBindings: onStates,
    },
    {
        id: "off-loading",
        label: "Off, loading",
        selector: "[data-unchecked][data-loading]",
        restingBindings: [
            ...offResting,
            thumbPosition(false),
            spinnerOpacity(true),
        ],
        stateBindings: {
            ...loadingStates,
            invalid: [
                edge(
                    "--border-default",
                    "Loading keeps Figma's spinner colours over the error border.",
                ),
            ],
            disabled: offStates.disabled,
        },
    },
    {
        id: "on-loading",
        label: "On, loading",
        selector: "[data-checked][data-loading]",
        restingBindings: [
            ...offResting,
            thumbPosition(true),
            spinnerOpacity(true),
        ],
        stateBindings: { ...loadingStates, disabled: onStates.disabled },
    },
];

export const switchAnatomy: ComponentAnatomySpec = {
    label: "Switch",
    schematic: "switch",
    rootSelector: ".switch.interactive",
    variantAxisLabel: "Value",
    parts,
    states,
    sharedBindings,
    variants,
    sizes: switchAnatomySizes,
};
